import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { CircleHelpIcon } from 'lucide-react';
import { useEffect, useMemo, useRef } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import * as z from 'zod';

import { CreateOrderDTO, OrderCurrency } from '@invenco/common-interface/sales';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/core/card';
import {
  ComboboxContent,
  ComboboxEmpty,
  ComboboxItem,
  ComboboxList,
} from '@/components/core/combobox';
import { Form } from '@/components/core/form';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/core/tooltip';
import { ComboboxFormField } from '@/components/forms/combobox-form-field';
import { SwitchFormField } from '@/components/forms/switch-form-field';
import { WizardStep } from '@/components/wizard';
import type { WizardStepDefinition } from '@/components/wizard/wizard-context';
import { SUPPORTED_CURRENCIES } from '@/constants';
import { useGateways } from '@/gateways/GatewayProvider';
import { useHasFormValues } from '@/shared/hooks/use-has-form-values';
import { useIsFormValid } from '@/shared/hooks/use-is-form-valid';

const propertiesSchema = z.object({
  channelName: z.literal('Default'),
  currency: z.enum(SUPPORTED_CURRENCIES),
  allowPartialFulfillments: z.boolean(),
  fulfillmentProfileId: z.string(),
});

const DEFAULT_FULFILLMENT_PROFILE_ID = '__default__';
const valueFields = ['channelName', 'currency'] as const;

type PropertiesFormValues = z.output<typeof propertiesSchema>;

type ComboboxOption = {
  value: string;
  label: string;
};

function ComboboxOptions({ emptyMessage }: { emptyMessage: string }) {
  return (
    <ComboboxContent>
      <ComboboxEmpty>{emptyMessage}</ComboboxEmpty>
      <ComboboxList>
        {(option: ComboboxOption) => (
          <ComboboxItem key={option.value ?? 'default'} value={option.value}>
            {option.label}
          </ComboboxItem>
        )}
      </ComboboxList>
    </ComboboxContent>
  );
}

export function PropertiesStep({
  step,
  onChange,
  onChannelNameChange,
  onCurrencyChange,
}: {
  step: WizardStepDefinition;
  onChange: (input: Partial<CreateOrderDTO>) => void;
  onChannelNameChange: (channelName: string) => void;
  onCurrencyChange: (currency: OrderCurrency) => void;
}) {
  const { salesGateway } = useGateways();
  const form = useForm<PropertiesFormValues>({
    resolver: zodResolver(propertiesSchema),
    defaultValues: {
      channelName: 'Default',
      currency: OrderCurrency.AUD,
      allowPartialFulfillments: false,
      fulfillmentProfileId: DEFAULT_FULFILLMENT_PROFILE_ID,
    },
  });
  const isValid = useIsFormValid(form, propertiesSchema);
  const hasValues = useHasFormValues(form, valueFields);
  const channelName = useWatch({ control: form.control, name: 'channelName' });
  const currency = useWatch({ control: form.control, name: 'currency' });
  const onChannelNameChangeRef = useRef(onChannelNameChange);
  const onCurrencyChangeRef = useRef(onCurrencyChange);

  onChannelNameChangeRef.current = onChannelNameChange;
  onCurrencyChangeRef.current = onCurrencyChange;

  useEffect(() => {
    onChannelNameChangeRef.current(channelName);
  }, [channelName]);

  useEffect(() => {
    onCurrencyChangeRef.current(currency);
  }, [currency]);

  const { data: channelData } = useQuery({
    queryKey: ['channels'],
    queryFn: ({ signal }) => salesGateway.getChannels(undefined, { signal }),
  });
  const { data: fulfillmentProfiles } = useQuery({
    queryKey: ['fulfillmentProfiles'],
    queryFn: ({ signal }) => salesGateway.getFulfillmentProfiles(undefined, { signal }),
    select: ({ items }) => items,
  });

  const selectedChannel = useMemo(
    () => channelData?.items.find((channel) => channel.name === channelName),
    [channelData?.items, channelName],
  );
  const profileOptions = useMemo<ComboboxOption[]>(() => {
    const filteredProfiles =
      fulfillmentProfiles?.filter((profile) => {
        if (!selectedChannel) return true;
        const locationIds = [
          ...new Set([...profile.fulfillmentLocationIds, profile.fallbackLocationId]),
        ];
        return selectedChannel.fulfillmentLocationIds.some((locationId) =>
          locationIds.includes(locationId),
        );
      }) ?? [];

    return [
      { value: DEFAULT_FULFILLMENT_PROFILE_ID, label: 'Default' },
      ...filteredProfiles.map((profile) => ({ value: profile.id, label: profile.name })),
    ];
  }, [fulfillmentProfiles, selectedChannel]);

  const submitSection = async () => {
    let valid = false;

    await form.handleSubmit((data) => {
      const selectedProfile =
        data.fulfillmentProfileId === DEFAULT_FULFILLMENT_PROFILE_ID
          ? undefined
          : fulfillmentProfiles?.find((profile) => profile.id === data.fulfillmentProfileId);

      onChange({
        channelName: data.channelName,
        currency: data.currency,
        allowPartialFulfillments: data.allowPartialFulfillments,
        fulfillmentProfileId: selectedProfile?.id ?? null,
        fulfillmentProfileName: selectedProfile?.name ?? null,
      });
      valid = true;
    })();

    return valid;
  };

  return (
    <WizardStep step={step} complete={isValid && hasValues} onSubmitStep={submitSection}>
      <Card aria-label="Properties">
        <CardHeader>
          <CardTitle>Properties</CardTitle>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form className="grid gap-4">
              <ComboboxFormField
                control={form.control}
                name="channelName"
                label="Channel"
                items={[{ value: 'Default', label: 'Default' }]}
                aria-label="Channel"
                disabled
              >
                <ComboboxOptions emptyMessage="No channels found" />
              </ComboboxFormField>
              <ComboboxFormField
                control={form.control}
                name="currency"
                label="Currency"
                items={SUPPORTED_CURRENCIES.map((currency) => ({
                  value: currency,
                  label: currency,
                }))}
                aria-label="Currency"
              >
                <ComboboxOptions emptyMessage="No currencies found" />
              </ComboboxFormField>
              <SwitchFormField
                control={form.control}
                name="allowPartialFulfillments"
                label={
                  <span className="inline-flex items-center gap-1.5">
                    Partial fulfillment
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <CircleHelpIcon
                          tabIndex={0}
                          aria-label="Partial fulfillment help"
                          className="size-4 text-muted-foreground"
                        />
                      </TooltipTrigger>
                      <TooltipContent>
                        Allows a fulfillment to be created with only partially available inventory.
                        This will automatically toggle off when the next fulfillment is created upon
                        order release.
                      </TooltipContent>
                    </Tooltip>
                  </span>
                }
                aria-label="Partial fulfillment"
              />
              <ComboboxFormField
                control={form.control}
                name="fulfillmentProfileId"
                label="Fulfillment profile"
                items={profileOptions}
                itemToStringLabel={(value) =>
                  profileOptions.find((option) => option.value === value)?.label ?? ''
                }
                aria-label="Fulfillment profile"
              >
                <ComboboxOptions emptyMessage="No fulfillment profiles found" />
              </ComboboxFormField>
            </form>
          </Form>
        </CardContent>
      </Card>
    </WizardStep>
  );
}
